/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize} from 'rxjs';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';

import template from './email-template-edit-page.component.html';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../../components/session/local-user-permissions';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {
  applyDefaultEmailTemplateContent,
  getEmailTemplateTeamQueryParams,
  initEmailTemplateModel,
  prepareEmailTemplateForSubmit,
  updateEmailTemplateInfo
} from './email-template-commons';
import {
  EmailTemplateApiService,
  EmailTemplateModel,
  EmailTemplateTeam
} from './email-template-api.service';
import {APP_SHARED} from '../../common/app-shared';
import {CodemirrorFieldComponent} from '../../common/directives/codemirror-field.component';

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, TranslateModule, ButtonsModule, CodemirrorFieldComponent],
  selector: 'adn-email-template-edit-page',
  template
})
export class EmailTemplateEditPageComponent extends SignalAdminResourceEditPageBase<EmailTemplateModel> {
  readonly isMarketplace: boolean;
  readonly allTeams = signal<EmailTemplateTeam[]>([]);
  readonly teamsLoaded = signal(false);

  private readonly emailApi: EmailTemplateApiService;

  constructor() {
    const api = inject(EmailTemplateApiService);
    super(api, 'emailTemplate');
    this.emailApi = api;
    this.isMarketplace = inject(LocalNetworkProfile).isPureMarketplacePlatform();
    const permissions = inject(LocalUserPermissions);
    this.emailApi.listTeams(getEmailTemplateTeamQueryParams(permissions)).pipe(
      finalize(() => this.teamsLoaded.set(true)),
      takeUntilDestroyed(inject(DestroyRef))
    ).subscribe({
      next: (page) => this.allTeams.set(page.results || []),
      error: () => undefined
    });
  }

  get showTeamField(): boolean {
    return this.isMarketplace || !!this.editingModel?.team?.id;
  }

  compareTeams(team1: EmailTemplateTeam, team2: EmailTemplateTeam): boolean {
    return team1 && team2 ? team1.id === team2.id : team1 === team2;
  }

  onTypeChange(): void {
    applyDefaultEmailTemplateContent(this.editingModel);
  }

  onSpecifyEmailTemplateChange(): void {
    applyDefaultEmailTemplateContent(this.editingModel);
    this.markExternalFormDirty();
  }

  protected override initialiseModel(model: EmailTemplateModel): EmailTemplateModel {
    const initialised = super.initialiseModel(model);
    initEmailTemplateModel(initialised);
    return initialised;
  }

  protected override prepareForSubmit(model: EmailTemplateModel): void {
    prepareEmailTemplateForSubmit(model);
  }

  protected override afterSave(model: EmailTemplateModel): void {
    updateEmailTemplateInfo(model);
    applyDefaultEmailTemplateContent(model);
  }
}
